import { useState, useEffect } from 'react';

/**
 * Custom hook for typewriter effect
 * Displays text character by character with configurable speed
 *
 * @param {string} text - The full text to display
 * @param {number} speed - Speed in milliseconds per character (default: 14ms)
 * @returns {Object} - { displayedText: string, isComplete: boolean }
 *
 * @example
 * const { displayedText, isComplete } = useTypewriter(message.response, 14);
 */
export function useTypewriter(text, speed = 14) {
  const [displayedText, setDisplayedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    // Reset when text changes
    setDisplayedText('');
    setCurrentIndex(0);
    setIsComplete(false);
  }, [text]);

  useEffect(() => {
    if (!text || currentIndex >= text.length) {
      setIsComplete(true);
      return;
    }

    const timeout = setTimeout(() => {
      setDisplayedText((prev) => prev + text[currentIndex]);
      setCurrentIndex((prev) => prev + 1);
    }, speed);

    return () => clearTimeout(timeout);
  }, [text, currentIndex, speed]);

  return { displayedText, isComplete };
}
